// V4.14 — Market Brief: welcome-back deltas since the last visit.
// V4.23 Polish — compact INLINE card inside the chat scroll region
// (never a floating overlay): collapsed by default to one slim row,
// expandable, dismissible once per browser session.
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bell, Car, ChevronDown, ChevronUp, HeartOff, Star, TrendingDown, X,
} from "lucide-react";
import { ackMarketBrief, fetchMarketBrief } from "../../api/aiApi";

const ITEM_ICONS = {
  profile_matches: Car,
  favorite_price_drops: TrendingDown,
  new_top_deals: Star,
  saved_search_matches: Bell,
  favorites_inactivated: HeartOff,
};

const SESSION_KEY = "da_market_brief_dismissed";

export const MarketBriefCard = () => {
  const { t } = useTranslation("ai");
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === "1",
  );
  const [expanded, setExpanded] = useState(false);
  const q = useQuery({
    queryKey: ["ai-market-brief"],
    queryFn: fetchMarketBrief,
    staleTime: 10 * 60 * 1000,
    retry: false,
    enabled: !dismissed,
  });
  if (dismissed) return null;
  const data = q.data;
  const items = data?.items || [];
  if (!data?.show || !items.length) return null;

  const dismiss = (e) => {
    e.stopPropagation();
    sessionStorage.setItem(SESSION_KEY, "1");
    setDismissed(true);
    ackMarketBrief().catch(() => {});
  };
  const itemLabel = (it) =>
    t(it.label ? `brief.items.${it.type}_labeled` : `brief.items.${it.type}`, {
      count: it.count,
      label: it.label,
    });

  return (
    <div
      data-testid="ai-market-brief-card"
      className="animate-in fade-in duration-300 flex items-center gap-1 rounded-xl border border-amber-500/25 bg-gradient-to-r from-amber-500/10 to-zinc-900/80 pr-2"
    >
      <div className="min-w-0 flex-1">
        <button
          type="button"
          data-testid="ai-market-brief-toggle"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 px-3 py-2 text-left"
        >
          <span
            className="min-w-0 flex-1 truncate text-xs font-medium text-zinc-100"
            data-testid="ai-market-brief-welcome"
          >
            {data.first_name
              ? t("brief.welcomeName", { name: data.first_name })
              : t("brief.welcome")}
          </span>
          <span className="shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-semibold text-amber-300">
            {t("brief.updatesCount", { count: items.length })}
          </span>
          {expanded
            ? <ChevronUp className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
            : <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-500" />}
        </button>
        {expanded && (
          <ul className="space-y-1.5 px-3 pb-3" data-testid="ai-market-brief-items">
            {items.map((it) => {
              const Icon = ITEM_ICONS[it.type] || Star;
              return (
                <li
                  key={it.type}
                  data-testid={`ai-market-brief-item-${it.type}`}
                  className="flex items-center gap-2 text-xs text-zinc-200"
                >
                  <Icon className="h-3.5 w-3.5 shrink-0 text-amber-400" />
                  <span>{itemLabel(it)}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <button
        type="button"
        data-testid="ai-market-brief-dismiss"
        onClick={dismiss}
        title={t("brief.dismiss")}
        className="shrink-0 self-start rounded-md p-1.5 mt-1.5 text-zinc-500 transition hover:bg-zinc-800/70 hover:text-zinc-200"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
